const WEEKDAYS = [
  { value: 1, short: 'Mon' },
  { value: 2, short: 'Tue' },
  { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' },
  { value: 6, short: 'Sat' },
  { value: 0, short: 'Sun' },
];

interface WeeklyOffDaysPickerProps {
  /** `null` (or omitted) means "inherit the payroll policy". */
  value?: number[] | null;
  onChange: (value: number[] | null) => void;
  disabled?: boolean;
}

/**
 * Employee-level weekly off-day picker. Each employee may have a different
 * weekend (e.g. Friday vs Sunday), which changes the days that count as
 * paid off days and the days an unapproved absence can be deducted from.
 */
export function WeeklyOffDaysPicker({ value, onChange, disabled }: WeeklyOffDaysPickerProps) {
  const inheriting = !Array.isArray(value);
  const selected = Array.isArray(value) ? value : [];

  const toggle = (day: number) => {
    const next = selected.includes(day)
      ? selected.filter((d) => d !== day)
      : [...selected, day].sort((a, b) => a - b);
    // An empty selection means "no override", not "no off days", so the
    // employee falls back to the payroll policy.
    onChange(next.length ? next : null);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {WEEKDAYS.map((day) => {
          const isSelected = selected.includes(day.value);
          return (
            <button
              key={day.value}
              type="button"
              disabled={disabled}
              onClick={() => toggle(day.value)}
              aria-pressed={isSelected}
              className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 ${
                isSelected
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-text-muted hover:border-primary/30 hover:text-text'
              }`}
            >
              {day.short}
            </button>
          );
        })}
        {!inheriting && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(null)}
            className="px-3 py-1.5 rounded-lg border border-dashed border-border text-xs font-medium text-text-muted hover:border-primary/40 hover:text-text transition-colors disabled:opacity-50"
          >
            Inherit policy
          </button>
        )}
      </div>
      <p className="text-xs text-text-muted">
        {inheriting
          ? 'Using the payroll policy off days. Pick days here to override them for this employee.'
          : 'These days are paid weekly offs for this employee and are never deducted as absence.'}
      </p>
    </div>
  );
}
